import React, { useMemo } from "react";
import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { FieldValues, FormState, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { Row, Col } from "react-bootstrap";
import Typography from "@mui/material/Typography";
import { Entity } from "./shared/interfaces/AccessControl";
import { AccessControlContext } from "./contexts/AccessControlContext";
import { Customer } from "./shared/interfaces/Customer";
import { LOCALSTORAGE_KEYS } from "./shared/constants";
import { ValidationMessage } from "./shared/ValidationMessage";
import { Autocomplete, TextField } from "@mui/material";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";

interface Props {
  selectedCustomerId: number | undefined;
  entity: Entity;
  onSelect: (customer?: Customer) => void;
  className?: string;
  hideLabel?: boolean;
  register?: UseFormRegister<FieldValues>;
  formState?: FormState<FieldValues>;
  setValue?: UseFormSetValue<FieldValues>;
  displaySingleCustomerName?: boolean;
}

const customerStorageKey = LOCALSTORAGE_KEYS.customer;
const hvSubstationSortFieldStorageKey = LOCALSTORAGE_KEYS.hvSubstationSortField;
const hvSubstationSortOrderStorageKey = LOCALSTORAGE_KEYS.hvSubstationSortOrder;
const workflowsSortFieldStorageKey = LOCALSTORAGE_KEYS.workflowsSortField;
const workflowsSortOrderStorageKey = LOCALSTORAGE_KEYS.workflowsSortOrder;
const workOrdersSortFieldStorageKey = LOCALSTORAGE_KEYS.workOrdersSortField;
const workOrdersSortOrderStorageKey = LOCALSTORAGE_KEYS.workOrdersSortOrder;
const resultsSortFieldStorageKey = LOCALSTORAGE_KEYS.resultsSortField;
const resultsSortOrderStorageKey = LOCALSTORAGE_KEYS.resultsSortOrder;
const companiesSortFieldStorageKey = LOCALSTORAGE_KEYS.companiesSortField;
const companiesSortOrderStorageKey = LOCALSTORAGE_KEYS.companiesSortOrder;
const sitesSortFieldStorageKey = LOCALSTORAGE_KEYS.sitesSortField;
const sitesSortOrderStorageKey = LOCALSTORAGE_KEYS.sitesSortOrder;

export const CustomersByEntityDropdown = React.forwardRef<typeof Autocomplete, Props>((props, ref) => {
  const { t } = useTranslation();
  const accessControlContext = useContext(AccessControlContext);

  const [customersList, setCustomersList] = useState<Customer[] | undefined>();

  const { selectedCustomerId, entity, onSelect, register, formState, setValue, hideLabel, displaySingleCustomerName } =
    props;

  const selectedCustomer = useMemo(
    () => customersList?.find((customer) => customer.id === selectedCustomerId) ?? null,
    [selectedCustomerId, customersList],
  );

  useEffect(() => {
    let customers = accessControlContext.getCustomerListByEntity(entity);

    setCustomersList(customers);

    if (customers && customers.length === 1) {
      onSelect(customers[0]);
    }
  }, [accessControlContext, entity, onSelect, t]);

  useEffect(() => {
    const customerId = localStorage.getItem(customerStorageKey);
    if (!selectedCustomer) {
      const storedCustomer = customersList?.find((customer: Customer) => customer.id.toString() === customerId);
      storedCustomer && onSelect(storedCustomer);
    }
  }, [customersList, selectedCustomer, onSelect]);

  function handleChange(e: React.SyntheticEvent, value: Customer | null) {
    if (value) {
      localStorage.setItem(customerStorageKey, value.id.toString());
      localStorage.removeItem(LOCALSTORAGE_KEYS.hvSubstation);
      localStorage.removeItem(hvSubstationSortFieldStorageKey);
      localStorage.removeItem(hvSubstationSortOrderStorageKey);
      localStorage.removeItem(workflowsSortFieldStorageKey);
      localStorage.removeItem(workflowsSortOrderStorageKey);
      localStorage.removeItem(workOrdersSortFieldStorageKey);
      localStorage.removeItem(workOrdersSortOrderStorageKey);
      localStorage.removeItem(resultsSortFieldStorageKey);
      localStorage.removeItem(resultsSortOrderStorageKey);
      localStorage.removeItem(companiesSortFieldStorageKey);
      localStorage.removeItem(companiesSortOrderStorageKey);
      localStorage.removeItem(sitesSortFieldStorageKey);
      localStorage.removeItem(sitesSortOrderStorageKey);

      setValue && setValue(`customer`, value, { shouldValidate: true });
      onSelect(value);
    }
  }

  if (customersList && customersList.length === 1 && displaySingleCustomerName) return <>{selectedCustomer?.name}</>;
  else if (customersList && customersList.length > 1)
    return (
      <React.Fragment>
        <Row className="d-flex flex-column">
          {!hideLabel && (
            <Col xs={"auto"} className="d-flex align-items-center">
              <Typography variant="subtitle2" color="textPrimary">
                {t("customer.customer")}:{" "}
              </Typography>
            </Col>
          )}
          <Col xs={"auto"} className="d-flex">
            {register ? (
              <div>
                <Autocomplete
                  autoHighlight
                  blurOnSelect
                  id="customerDropdown"
                  className={formState && formState.errors["customer"] ? "p-invalid" : undefined}
                  value={selectedCustomer}
                  options={customersList}
                  renderInput={(params) => <TextField {...params} placeholder={t("customer.selectCustomer")} />}
                  renderOption={(props, option, { inputValue }) => {
                    const matches = match(option.name, inputValue, {
                      insideWords: true,
                    });
                    const parts = parse(option.name, matches);
                    return (
                      <li {...props}>
                        <div>
                          {parts.map((part, index) => (
                            <span
                              key={index}
                              style={{
                                fontWeight: part.highlight ? 700 : 400,
                              }}
                            >
                              {part.text}
                            </span>
                          ))}
                        </div>
                      </li>
                    );
                  }}
                  size="small"
                  sx={{ width: 200, bgcolor: "white" }}
                  getOptionLabel={(option) => option.name}
                  {...register(`customer`, {
                    value: selectedCustomer,
                    required: true,
                    validate: {
                      validCustomer: (value) => value && value.id !== 0 && value.name !== t("common.allCustomers"),
                    },
                  })}
                  onChange={handleChange}
                  ref={ref}
                />
                {formState && formState.errors["customer"] && (
                  <ValidationMessage
                    invalidMessage={t("common.pleaseEnter", {
                      val: "$t(customer.customer)",
                    })}
                    validationType="error"
                    showIcon
                  />
                )}
              </div>
            ) : (
              <Autocomplete
                ref={ref}
                autoHighlight
                blurOnSelect
                id="customerDropdown"
                className={formState && formState.errors["customer"] ? "p-invalid" : undefined}
                value={selectedCustomer}
                options={customersList}
                disableClearable={selectedCustomer != null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("customer.selectCustomer")}
                    fullWidth
                    sx={{ width: 250, bgcolor: "white" }}
                  />
                )}
                renderOption={(props, option, { inputValue }) => {
                  const matches = match(option.name, inputValue, {
                    insideWords: true,
                  });
                  const parts = parse(option.name, matches);
                  return (
                    <li {...props}>
                      <div>
                        {parts.map((part, index) => (
                          <span
                            key={index}
                            style={{
                              fontWeight: part.highlight ? 700 : 400,
                            }}
                          >
                            {part.text}
                          </span>
                        ))}
                      </div>
                    </li>
                  );
                }}
                size="small"
                onChange={handleChange}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            )}
          </Col>
        </Row>
      </React.Fragment>
    );
  else return null;
});
